import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Group, PasswordInput, Stack, Text, TextInput } from "@mantine/core";
import qrcode from "qrcode-generator";
import { usePublishStatus, useResetSetting } from "../api/admin";
import { api, ApiError } from "../api/client";
import type { Setting } from "../api/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Chip } from "../ui/Chip";
import { StatePill } from "../ui/StatePill";
import { useNow } from "../time/useNow";
import { FeedbackLine } from "../ui/FeedbackLine";

/**
 * The two sign-ins, in the settings row that holds the credential they produce.
 *
 * Both platforms are signed into as a *user*, so what these write is a session, not a token
 * somebody can paste. Replacing the raw password box for that one key with the sign-in itself
 * is what keeps the page honest: the row that holds the session *is* how you get one.
 *
 * Neither control offers api_id, api_hash or the community id. Those are their own rows on
 * the same page, so a typo can be corrected without a fresh sign-in, and duplicating them
 * here would be two controls writing one value.
 */

function useAfterSignIn() {
  const client = useQueryClient();
  return useCallback(() => {
    void client.invalidateQueries({ queryKey: ["settings"] });
    void client.invalidateQueries({ queryKey: ["publish", "status"] });
  }, [client]);
}

// `quiet` because "signed in" is the resting state, not something happening right now:
// `live` is the danger hue and StatePill reserves it for that.

function LoginRow({
  account,
  error,
  children,
}: {
  account: string | null | undefined;
  // Every sign-in prints its refusals in the same place, at the full width of the row and
  // under whatever control asked for them.
  error?: string | null;
  // Optional: a row whose status has not arrived yet offers nothing.
  children?: React.ReactNode;
}) {
  const { t } = useTranslation("admin");

  // `SettingShell` draws the row this sits in -- the name, the key and the description are
  // its. What is left is the control: one flow-laid group, because a QR plus its code is
  // wider and taller than a text field.
  return (
    <Group gap={8}>
      {account && <StatePill tone="quiet">{t("publishLogin.signedInPill")}</StatePill>}
      {children}
      <FeedbackLine role="alert" w="100%">
        {error}
      </FeedbackLine>
    </Group>
  );
}

interface TelegramLoginOut {
  status: string;
  detail: string | null;
  account: string | null;
}

export function TelegramLogin({ setting }: { setting: Setting }) {
  const { t } = useTranslation("admin");
  const { data: statuses } = usePublishStatus();
  const reset = useResetSetting();
  const done = useAfterSignIn();
  const status = statuses?.find((row) => row.name === "telegram");

  const [phone, setPhone] = useState("");
  // Free text, never a numeric input: Telegram has SmsWord and SmsPhrase code types, so a
  // digits-only field would be one an operator simply cannot complete.
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [stage, setStage] = useState<"idle" | "sent" | "password_needed">("idle");
  const [detail, setDetail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (body: { phone: string; code?: string; password?: string }) => {
    setBusy(true);
    setError(null);
    try {
      const out = await api.post<TelegramLoginOut>("/api/publish/telegram/login", body);
      setDetail(out.detail);
      if (out.status === "signed_in") {
        setStage("idle");
        setCode("");
        setPassword("");
        done();
      } else if (out.status === "password_needed") {
        setStage("password_needed");
      } else {
        setStage("sent");
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : String(caught));
    } finally {
      setBusy(false);
    }
  };

  // Until the status query answers, `blocker` is undefined and none of the branches below
  // match -- which offers a phone form to an operator who has not set an api pair yet, and
  // whose Send code would only 409. Show the row, offer nothing.
  if (!statuses) return <LoginRow account={null} />;

  if (status?.account) {
    return (
      <LoginRow account={status.account}>
        <span>{t("publishLogin.signedInAs", { account: status.account })}</span>
        <Chip
          onClick={() =>
            void reset.mutateAsync(setting.key).then(() => {
              setStage("idle");
              setDetail(null);
              done();
            })
          }
        >
          {t("publishLogin.signOut")}
        </Chip>
      </LoginRow>
    );
  }

  // `blocker` is a stable identifier on the wire, never translated: compare it, do not
  // "fix" this into a catalogue lookup. Displayed blockers are translated at the render
  // site via `publishTargets.blocker.*`.
  if (status?.blocker === "no api_id/api_hash") {
    return (
      <LoginRow account={null}>
        <Text component="span" c="dimmed" fz="xs">
          {t("publishLogin.setApiKeysFirst")}
        </Text>
      </LoginRow>
    );
  }

  return (
    <LoginRow account={null} error={error}>
      <TextInput
        w="100%"
        placeholder={t("publishLogin.phonePlaceholder")}
        autoComplete="tel"
        value={phone}
        spellCheck={false}
        onChange={(event) => setPhone(event.target.value)}
      />
      {stage === "idle" && (
        <Chip
          variant="accent"
          disabled={busy || phone.trim() === ""}
          onClick={() => void submit({ phone: phone.trim() })}
        >
          {busy ? t("publishLogin.sending") : t("publishLogin.sendCode")}
        </Chip>
      )}
      {stage === "sent" && (
        <>
          <TextInput
            w="100%"
            placeholder={t("publishLogin.codePlaceholder")}
            autoComplete="one-time-code"
            value={code}
            spellCheck={false}
            onChange={(event) => setCode(event.target.value)}
          />
          <Chip
            variant="accent"
            disabled={busy || code.trim() === ""}
            onClick={() => void submit({ phone: phone.trim(), code: code.trim() })}
          >
            {busy ? t("publishLogin.signingIn") : t("publishLogin.signIn")}
          </Chip>
        </>
      )}
      {stage === "password_needed" && (
        <>
          <PasswordInput
            w="100%"
            placeholder={t("publishLogin.passwordPlaceholder")}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <Chip
            variant="accent"
            disabled={busy || password === ""}
            onClick={() => void submit({ phone: phone.trim(), code: code.trim(), password })}
          >
            {busy ? t("publishLogin.signingIn") : t("publishLogin.signIn")}
          </Chip>
        </>
      )}
      {detail && (
        <Text component="span" c="dimmed" fz="xs">
          {detail}
        </Text>
      )}
    </LoginRow>
  );
}

interface VkQrOut {
  flow_id: string;
  auth_url: string;
  auth_code: string;
  expires_at: number;
}

interface VkPollOut {
  status: string;
  detail: string | null;
  account: string | null;
}

/**
 * QR, and only QR.
 *
 * The phone-number route is behind VK's SmartCaptcha and the password grant answers HTTP 401
 * "9;Flood control" for every account — both measured — so a form with a password field
 * would be a control that never works.
 *
 * The QR is drawn here from the auth URL rather than fetched as an image: for the three
 * minutes it lives, that URL *is* the sign-in, and handing it to an external QR service would
 * be handing over the account.
 */
// No `setting` prop, unlike TelegramLogin: signing out of VK is its own endpoint rather than
// a reset of the row that holds the cookie jar, so this control never names the key.

/** Register one active QR status read with the app-wide refresh scheduler. */
function useVkQrPoll(
  flow: VkQrOut | null,
  setState: (out: VkPollOut) => void,
  setFlow: (flow: VkQrOut | null) => void,
  setError: (message: string) => void,
  done: () => void,
) {
  const flowId = flow?.flow_id;
  const poll = useQuery({
    queryKey: ["publish", "vk-qr", flowId ?? null],
    queryFn: ({ signal }) => api.get<VkPollOut>(`/api/publish/vk/qr/${flowId}`, signal),
    enabled: flowId !== undefined,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(() => {
    if (!poll.data) return;
    setError("");
    setState(poll.data);
    if (poll.data.status === "signed_in") {
      setFlow(null);
      done();
    }
  }, [done, poll.data, setError, setFlow, setState]);

  useEffect(() => {
    if (poll.error) {
      setError(poll.error instanceof ApiError ? poll.error.detail : String(poll.error));
    }
  }, [poll.error, setError]);

  useEffect(() => {
    if (poll.data?.status !== "expired") return;
    let active = true;
    void api
      .post<VkQrOut>("/api/publish/vk/qr")
      .then((fresh) => {
        if (active) setFlow(fresh);
      })
      .catch((caught) => {
        if (active) setError(caught instanceof ApiError ? caught.detail : String(caught));
      });
    return () => {
      active = false;
    };
  }, [flowId, poll.data?.status, setError, setFlow]);
}

/**
 * The QR sign-in itself: start a flow, answer a code, sign out.
 *
 * A hook rather than more of the control below, because none of this is about what is on
 * screen: the control reads the flow it returns and never touches an endpoint of its own.
 */
function useVkQrFlow() {
  const done = useAfterSignIn();

  const [flow, setFlow] = useState<VkQrOut | null>(null);
  const [state, setState] = useState<VkPollOut | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const now = useNow(1000);

  const start = async () => {
    setBusy(true);
    setError(null);
    setState(null);
    setCode("");
    try {
      setFlow(await api.post<VkQrOut>("/api/publish/vk/qr"));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : String(caught));
    } finally {
      setBusy(false);
    }
  };

  useVkQrPoll(flow, setState, setFlow, setError, done);

  const submitCode = async () => {
    if (flow === null) return;
    setBusy(true);
    setError(null);
    try {
      const out = await api.post<VkPollOut>("/api/publish/vk/qr/code", {
        flow_id: flow.flow_id,
        code: code.trim(),
      });
      setState(out);
      if (out.status === "signed_in") {
        setFlow(null);
        done();
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : String(caught));
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    setBusy(true);
    try {
      await api.post("/api/publish/vk/signout");
      setFlow(null);
      setState(null);
      done();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.detail : String(caught));
    } finally {
      setBusy(false);
    }
  };

  return { flow, state, code, setCode, error, busy, now, start, submitCode, signOut };
}

export function VkLogin() {
  const { t } = useTranslation("admin");
  const { data: statuses } = usePublishStatus();
  const status = statuses?.find((row) => row.name === "vk");
  const { flow, state, code, setCode, error, busy, now, start, submitCode, signOut } =
    useVkQrFlow();

  // As in TelegramLogin: an unresolved status must not offer a fresh sign-in to an account
  // that is already signed in.
  if (!statuses) return <LoginRow account={null} />;

  if (status?.account && flow === null) {
    return (
      <LoginRow account={status.account}>
        <span>{t("publishLogin.signedInAs", { account: status.account })}</span>
        <Chip disabled={busy} onClick={() => void signOut()}>
          {t("publishLogin.signOut")}
        </Chip>
      </LoginRow>
    );
  }

  if (flow === null) {
    return (
      <LoginRow account={null} error={error}>
        <Chip variant="accent" disabled={busy} onClick={() => void start()}>
          {busy ? t("publishLogin.starting") : t("publishLogin.signInWithQr")}
        </Chip>
      </LoginRow>
    );
  }

  // Risk-based and often skipped entirely — one measured sign-in went straight from waiting
  // to signed in — so this is never a required step of the state machine.
  if (state?.status === "code_needed") {
    return (
      <LoginRow account={null} error={error}>
        <Text component="span" c="dimmed" fz="xs">
          {state.detail ?? t("publishLogin.vkCodePrompt")}
        </Text>
        <TextInput
          w="100%"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={t("publishLogin.vkCodePlaceholder")}
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <Chip
          variant="accent"
          disabled={busy || code.trim() === ""}
          onClick={() => void submitCode()}
        >
          {busy ? t("publishLogin.confirming") : t("publishLogin.confirm")}
        </Chip>
      </LoginRow>
    );
  }

  const remaining = Math.max(0, Math.round(flow.expires_at - now / 1000));
  return (
    <LoginRow account={null} error={error}>
      <img
        src={qrDataUrl(flow.auth_url)}
        alt=""
        width={160}
        height={160}
        style={{ display: "block", imageRendering: "pixelated" }}
      />
      <Stack gap={4}>
        <Text component="span" c="dimmed" fz="xs">
          {t("publishLogin.scanWithVkApp")}
        </Text>
        <Text component="code" ff="monospace" fz="md" lts="0.08em">
          {flow.auth_code}
        </Text>
        <Text component="span" c="dimmed" fz="xs">
          {t("publishLogin.secondsLeft", { seconds: remaining })}
        </Text>
      </Stack>
    </LoginRow>
  );
}

function qrDataUrl(url: string): string {
  const q = qrcode(0, "M");
  q.addData(url);
  q.make();
  // Sized at the render site rather than by the module size, which stays a scan-quality
  // decision: 6 pixels a module with an 8-module quiet zone is what scans reliably.
  return q.createDataURL(6, 8);
}
